import React, { useEffect, useState, useRef } from 'react';
import { Field, Form as FinalForm } from 'react-final-form';

import { GiphyFetch } from '@giphy/js-fetch-api';
import EmojiPicker from 'emoji-picker-react';
import ReactHlsPlayer from 'react-hls-player';
import { FormattedMessage } from '../../../../util/reactIntl';

import {
  Button,
  FieldTextInput,
  Form,
  H4,
  ImageFromFile,
  OutsideClickHandler,
  ResponsiveImage,
} from '../../../../components';

import ProfileIconCard from '../../../../components/ProfileIconCard/ProfileIconCard';

import { abbreviateName, excludedKeysOfCreatePost, updatePostFormValues } from '../../../../util/circleHelper';
import { required } from '../../../../util/validators';

import FileUploadModal from './FileUploadModal';
import GifUploadModal from './GifUploadModal';
import ImageUploadModal from './ImageUploadModal';
import SelectTopicComponent from './SelectTopicComponent';
import VideoUploadModal from './VideoUploadModal';

import css from './CreatePostForm.module.css';
import CircleBlockEditor from '../../../../components/CircleBlockEditor/CircleBlockEditor';
import EditorOptionDropdown from './EditorOptionDropdown';
import { convertToRaw, RichUtils } from 'draft-js';
import CoverImageUploadModal from './CoverImageUploadModal';

const gf = new GiphyFetch(process.env.REACT_APP_GIPHY_API_KEY);

const CreatePostForm = props => (
  <FinalForm
    {...props}
    render={fieldRenderProps => {
      const {
        form,
        handleSubmit,
        invalid,
        spaceListData,
        isEmoji,
        setIsEmoji,
        values,
        requestDirectUpload,
        onGiphyChange,
        onDocumentClick,
        onImageClick,
        onVideoClick,
        isGiphyOpen,
        isDocumentModal,
        isImageModal,
        isVideoUpload,
        formOldValues,
        setFormOldValues,
        onFetchTopics,
        spaceTopicsData,
        advancedSearchData,
        onAdvancedSearch,
        params,
        editorState,
        setEditorState,
        insertEmoji,
        isCoverImageModal,
        onCoverImageClick,
      } = fieldRenderProps;
      const { id } = params || {};
      const [isSpaceDropdownVisible, setIsSpaceDropdownVisible] = useState(false);
      const [isEdited, setIsEdited] = useState(false);
      const [isEditorOptionOpen, setIsEditorOptionOpen] = useState(false);
      const hasAttachment =
        Boolean(formOldValues.image.length) ||
        Boolean(formOldValues.gifs.length) ||
        Boolean(formOldValues.video.length) ||
        Boolean(formOldValues.document.length);

      const validationMessage = <FormattedMessage id="CreatePost.validationMessage" />;

      const handleEmoji = emoji => {
        insertEmoji(emoji.emoji);
        setIsEmoji(false);
        return;
      };

      // Function to toggle the visibility of the box
      const toggleBox = () => {
        setIsSpaceDropdownVisible(!isSpaceDropdownVisible);
      };

      const handleSpaceSelection = spaceId => {
        console.log('spaceId', spaceId);
        updatePostFormValues(spaceId, setFormOldValues, 'spaceId');
        updatePostFormValues([], setFormOldValues, 'topics');

        onFetchTopics(spaceId);
        setIsSpaceDropdownVisible(false);
      };

      useEffect(() => {
        handleSpaceSelection(id);
      }, [id]);

      const selectedSpaceName = spaceListData?.find(item => item.id == values.spaceId)?.name;
      const handleRemoveItem = (item, type, fileType) => {
        setFormOldValues(prevState => {
          const updatedValues = {
            ...prevState,
            [type]: prevState[type]?.filter(value =>
              item.signed_id ? value.signed_id !== item.signed_id : value !== item
            ),
            ...(fileType && {
              [fileType]: prevState[fileType]?.filter(value => value !== item),
            }),
          };

          return updatedValues;
        });
      };

      const playerRef = useRef(null);

      useEffect(() => {
        if (playerRef.current) {
          const video = playerRef.current.querySelector('video');
          if (video) {
            video.setAttribute('playsinline', 'true');
            video.setAttribute('muted', 'true'); // Mute for Safari autoplay policies
          }
        }
      }, [playerRef]);

      const toggleDropdown = () => {
        setIsEditorOptionOpen(prev => !prev);
      };

      const toggleBlockType = blockType => {
        setEditorState(RichUtils.toggleBlockType(editorState, blockType));
      };

      const toggleInlineStyle = inlineStyle => {
        setEditorState(RichUtils.toggleInlineStyle(editorState, inlineStyle));
      };

      const getCurrentBlockType = () => {
        const selection = editorState.getSelection();
        const content = editorState.getCurrentContent();
        const block = content.getBlockForKey(selection.getStartKey());
        return block.getType();
      };

      const currentBlockType = getCurrentBlockType();

      const handleChange = event => {
        setFormOldValues(prevState => {
          const updatedValues = {
            ...prevState,
            postTitle: event?.target?.value,
          };
          return updatedValues;
        });
      };

    const handleRemoveCoverImage = () => {
      setFormOldValues(prevState => {
        const updatedValues = {
          ...prevState,
          coverImage: null,
          coverImagefiles: null,
        };
        return updatedValues;
      });
    }

    const handleEditorChange = newEditorState => {
      setEditorState(newEditorState);
      // Convert editor content to raw format and check if there's meaningful text
      const content = convertToRaw(newEditorState.getCurrentContent());
      const hasText = content.blocks.some(block => block.text.trim().length > 0);

      setIsEdited(hasText);
    };
    const { spaceId = '', topics = [] } = values ?? {}; 
    const isFilled = Object.entries(values).some(([key, value]) => {
      if (excludedKeysOfCreatePost.includes(key)) return false;
    
      if (Array.isArray(value)) return value.length > 0;
      if (typeof value === "object" && value !== null) return Object.keys(value).length > 0;
    
      return value !== undefined && value !== "" && value !== null;
    });
    const isTopicSelectionRequired = Object.values(spaceListData).find(item => item.id === Number(spaceId))?.require_topic_selection ?? false
    const isValid = (isFilled || isEdited) && (!isTopicSelectionRequired || (isTopicSelectionRequired && topics.length > 0)) &&  spaceId !== ''

      return (
        <Form onSubmit={handleSubmit}>
          <div className={css.createPostTop}>
            <div className={css.topHeadingWrapper}>
              <H4>Create post</H4>
              <div onClick={onCoverImageClick}><ProfileIconCard type="cover"/></div>
            </div>
            <div className={css.postTitleWrapper}>
              {values?.coverImagefiles?.file ? <div className={css.imageCoverWrapper}>
                <div
                  className={css.removeIcon}
                  onClick={handleRemoveCoverImage}
                >
                  <ProfileIconCard type="cross" />
                </div>
                <ImageFromFile
                  id={values?.coverImagefiles?.file}
                  className={''}
                  file={values?.coverImagefiles?.file}
                  aspectWidth={1}
                  aspectHeight={1}
                />
              </div> : null}
              <div className={css.inputPostWrapper}>
                <input
                  className={css.postTitleField}
                  id="postTitle"
                  type="text"
                  value={formOldValues?.postTitle}
                  onChange={handleChange}
                  placeholder="Type something..."
                  maxLength={250}
                />
                <div className={css.suggestField}>
                  <CircleBlockEditor
                    hasAttachment={hasAttachment}
                    editorState={editorState}
                    setEditorState={handleEditorChange}
                    advancedSearchData={advancedSearchData}
                    onAdvancedSearch={onAdvancedSearch}
                  />
                </div>
              </div>

              {/* render items from edit comment section */}
              <div className={css.imageuploadedWrapper}>
                {Array.isArray(values?.image)
                  ? values?.image.map(file => {
                      if (file?.image_variants?.thumbnail) {
                        return (
                          <div>
                            <div
                              className={css.removeIcon}
                              onClick={() => handleRemoveItem(file, 'image', '')}
                            >
                              <ProfileIconCard type="cross" />
                            </div>
                            <ResponsiveImage imagewithURL={file?.image_variants?.thumbnail} />
                          </div>
                        );
                      }
                    })
                  : null}
              </div>
              <div className={css.videoWrapper}>
                {Array.isArray(values?.gifs)
                  ? values?.gifs.map(file => {
                      if (file?.video_variants?.hls)
                        return (
                          <div>
                            <div
                              className={css.removeIcon}
                              onClick={() => handleRemoveItem(file, 'gifs', '')}
                            >
                              <ProfileIconCard type="cross" />
                            </div>
                            <ReactHlsPlayer
                              playerRef={playerRef}
                              src={file?.video_variants?.hls}
                              autoPlay={true}
                              controls={true}
                              width="100%"
                              height="auto"
                              playsInline
                              // onEnded={handleVideoEnd}
                              poster={file?.image_variants?.thumbnail}
                            />
                          </div>
                        );
                    })
                  : null}
              </div>
              <div className={css.videoWrapper}>
                {Array.isArray(values?.video)
                  ? values?.video.map(file => {
                      if (file?.video_variants?.hls)
                        return (
                          <div>
                            <div
                              className={css.removeIcon}
                              onClick={() => handleRemoveItem(file, 'video', '')}
                            >
                              <ProfileIconCard type="cross" />
                            </div>
                            <ReactHlsPlayer
                              src={file?.video_variants?.hls}
                              autoPlay={true}
                              controls={true}
                              width="100%"
                              height="auto"
                              // onEnded={handleVideoEnd}
                              poster={file?.image_variants?.thumbnail}
                            />
                          </div>
                        );
                    })
                  : null}
              </div>
              <div className={css.fileUplaodedWrapper}>
                {Array.isArray(values?.document)
                  ? values?.document.map(file => {
                      return (
                        <div>
                          <div className={css.fileUploaded} key={file}>
                            <span>
                              <ProfileIconCard type="document" />
                              {file?.filename}
                            </span>
                            <div
                              className={css.removeIcon}
                              onClick={() => handleRemoveItem(file, 'document', '')}
                            >
                              <ProfileIconCard type="cross" />
                            </div>
                          </div>
                        </div>
                      );
                    })
                  : null}
              </div>

              {/* render images */}
              <div className={css.imageuploadedWrapper}>
                {Array.isArray(values?.imagefiles)
                  ? values?.imagefiles.map(file => {
                      return (
                        <div>
                          <div
                            className={css.removeIcon}
                            onClick={() => handleRemoveItem(file, 'image', 'imagefiles')}
                          >
                            <ProfileIconCard type="cross" />
                          </div>
                          <ImageFromFile
                            id={file.file.name}
                            className={''}
                            file={file.file}
                            aspectWidth={1}
                            aspectHeight={1}
                          />
                        </div>
                      );
                    })
                  : null}
              </div>
              {/* render gifs */}
              <div className={css.addedgif}>
                {Array.isArray(values?.giffiles)
                  ? values?.giffiles.map(file => {
                      return (
                        <div>
                          <div
                            className={css.removeIcon}
                            onClick={() => handleRemoveItem(file, 'gifs', 'giffiles')}
                          >
                            <ProfileIconCard type="cross" />
                          </div>
                          <img src={file.file} />
                        </div>
                      );
                    })
                  : null}
              </div>

              {/* render videos */}
              <div className={css.videoWrapper}>
                {Array.isArray(values?.videofiles)
                  ? values?.videofiles.map(file => {
                      return (
                        <div>
                          <div
                            className={css.removeIcon}
                            onClick={() => handleRemoveItem(file, 'video', 'videofiles')}
                          >
                            <ProfileIconCard type="cross" />
                          </div>
                          <video src={file.file} loop autoPlay muted />
                        </div>
                      );
                    })
                  : null}
              </div>
              {/* render documents */}
              {/* <div className={css.fileUplaodedWrapper}>
              {Array.isArray(values?.documentsfiles)
                ? values?.documentsfiles.map(file => {
                    return (
                      <div className={css.fileUploaded} key={file}>
                        {file.name}
                      </div>
                    );
                  })
                : null}
            </div> */}

              {/* {requestDirectUpload ? (
              <div className={css.loader}>
                <IconSpinner type={true} />
              </div>
            ) : null} */}
            </div>
            {/* select topic component */}
            <SelectTopicComponent
              spaceTopicsData={spaceTopicsData}
              setFormOldValues={setFormOldValues}
              formOldValues={formOldValues}
            />
            <div className={css.bottomFunctionMenu}>
              <div className={css.leftIconSection}>
                <div className={css.documentUpload}>
                  {/* //isEditorOptionOpen, setIsEditorOptionOpen */}
                  <OutsideClickHandler onOutsideClick={() => isEditorOptionOpen && setIsEditorOptionOpen(false)}>
                    <label className={css.uplaodIcon} onClick={toggleDropdown}>
                      <ProfileIconCard type="add_file" />
                    </label>
                    <div className={css.editDropdown}>
                      {isEditorOptionOpen ? (
                        <EditorOptionDropdown
                          toggleDropdown={toggleDropdown}
                          toggleBlockType={toggleBlockType}
                          toggleInlineStyle={toggleInlineStyle}
                          currentBlockType={currentBlockType}
                        />
                      ) : null}
                    </div>
                  </OutsideClickHandler>
                </div>
                <div className={css.inputField}>
                  <label className={css.uplaodIcon} onClick={onDocumentClick}>
                    <ProfileIconCard type="document_uplaod" />
                  </label>
                  {isDocumentModal ? (
                    <FileUploadModal
                      isDocumentModal={isDocumentModal}
                      onDocumentClick={onDocumentClick}
                      form={form}
                      values={values}
                      requestDirectUpload={requestDirectUpload}
                      formOldValues={formOldValues}
                      setFormOldValues={setFormOldValues}
                    />
                  ) : null}
                </div>

                <div className={css.gifSection}>
                  <div className={css.uplaodIcon} onClick={onGiphyChange}>
                    <ProfileIconCard type="gif" />
                  </div>

                  {isGiphyOpen ? (
                    <GifUploadModal
                      isGiphyOpen={isGiphyOpen}
                      onGiphyChange={onGiphyChange}
                      form={form}
                      values={values}
                      requestDirectUpload={requestDirectUpload}
                      formOldValues={formOldValues}
                      setFormOldValues={setFormOldValues}
                    />
                  ) : null}
                </div>
                <div className={css.inputField}>
                  <label htmlFor="fileInput" className={css.uplaodIcon} onClick={onImageClick}>
                    <ProfileIconCard type="image" />
                  </label>
                  {isImageModal ? (
                    <ImageUploadModal
                      isImageModal={isImageModal}
                      onImageClick={onImageClick}
                      form={form}
                      values={values}
                      requestDirectUpload={requestDirectUpload}
                      formOldValues={formOldValues}
                      setFormOldValues={setFormOldValues}
                    />
                  ) : null}
                </div>
                <div className={css.inputField}>
                  <label htmlFor="fileInput" className={css.uplaodIcon} onClick={onVideoClick}>
                    <ProfileIconCard type="video" />
                  </label>
                  <VideoUploadModal
                    isVideoUpload={isVideoUpload}
                    onVideoClick={onVideoClick}
                    form={form}
                    values={values}
                    requestDirectUpload={requestDirectUpload}
                    formOldValues={formOldValues}
                    setFormOldValues={setFormOldValues}
                  />
                </div>
                <OutsideClickHandler onOutsideClick={() => isEmoji && setIsEmoji(false)}>
                <div className={css.emojiSection}>
                  <div className={css.uplaodIcon} onClick={() => setIsEmoji(!isEmoji)}>
                    <ProfileIconCard type="emoji" />
                  </div>

                  <div className={css.emojiPick}>
                    {isEmoji ? <EmojiPicker onEmojiClick={handleEmoji} /> : null}
                  </div>
                </div>
                </OutsideClickHandler>
                {/* <div className={css.uplaodIcon} >
                  <ProfileIconCard type="marketplace" />
                </div> */}
              </div>
              <div className={css.rightFieldSection}>
                <Field
                  name="spaceId"
                  validate={required(validationMessage)}
                  render={({ meta }) => {
                    return (
                      <div className={css.spaceIdBox}>
                        <div className={css.selectInput} onClick={toggleBox}>
                          <span>
                            {selectedSpaceName || (
                              <FormattedMessage id="CreatePost.chooseSpacePlaceHolder" />
                            )}
                          </span>
                        </div>

                        {isSpaceDropdownVisible && (
                          <div className={css.searchSpaceBox}>
                            {spaceListData
                              ?.filter(item => item.is_member && item.policies.can_create_post)
                              .map(item => (
                                <div
                                  key={item.id}
                                  className={css.itemName}
                                  value={item.id}
                                  onClick={() => handleSpaceSelection(item.id)}
                                >
                                  <span
                                    className={
                                      item.policies.can_manage_space &&
                                      item.policies.can_invite_members
                                        ? css.myGroup
                                        : css.joinedGroup
                                    }
                                  />
                                  <span className={css.name}>{item.name}</span>
                                </div>
                              ))}
                          </div>
                        )}
                      </div>
                    );
                  }}
                />

                <Button type="submit" className={css.publishButton} disabled={!isValid}>
                  <FormattedMessage id="CreatePost.publish" />
                </Button>
              </div>
            </div>
            <CoverImageUploadModal
              isImageModal={isCoverImageModal}
              onImageClick={onCoverImageClick}
              form={form}
              values={values}
              requestDirectUpload={requestDirectUpload}
              formOldValues={formOldValues}
              setFormOldValues={setFormOldValues}
            />
          </div>
        </Form>
      );
    }}
  />
);

export default CreatePostForm;
